// 12 required numeric columns plus an optional evaporation pattern name.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export interface Aquifer {
  name: string;
  porosity: number;
  wiltingPoint: number;
  fieldCapacity: number;
  conductivity: number;
  conductSlope: number;
  tensionSlope: number;
  upperEvapFrac: number;
  lowerEvapDepth: number;
  gwRecession: number;
  bottomElev: number;
  waterTableElev: number;
  upperMoisture: number;
  /** Time pattern name for the upper-zone evaporation multiplier, or "" for none. */
  evapPattern: string;
}

export function readAquifers(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): Aquifer[] {
  return (raw.get("AQUIFERS") ?? [])
    .filter((row) => row[0])
    .map((row) => {
      const ctx = (field: string) => `[AQUIFERS] "${row[0]}" ${field}`;
      return {
        name: row[0],
        porosity: parseNum(row[1], 0, ctx("Porosity"), warnings),
        wiltingPoint: parseNum(row[2], 0, ctx("Wilting Point"), warnings),
        fieldCapacity: parseNum(row[3], 0, ctx("Field Capacity"), warnings),
        conductivity: parseNum(row[4], 0, ctx("Conductivity"), warnings),
        conductSlope: parseNum(row[5], 0, ctx("Conductivity Slope"), warnings),
        tensionSlope: parseNum(row[6], 0, ctx("Tension Slope"), warnings),
        upperEvapFrac: parseNum(row[7], 0, ctx("Upper Evap Fraction"), warnings),
        lowerEvapDepth: parseNum(row[8], 0, ctx("Lower Evap Depth"), warnings),
        gwRecession: parseNum(row[9], 0, ctx("Gw Recession"), warnings),
        bottomElev: parseNum(row[10], 0, ctx("Bottom Elevation"), warnings),
        waterTableElev: parseNum(row[11], 0, ctx("Water Table Elevation"), warnings),
        upperMoisture: parseNum(row[12], 0, ctx("Upper Moisture"), warnings),
        evapPattern: row[13] ?? "",
      };
    });
}

export function writeAquifers(
  raw: ReadonlyMap<string, string[][]>,
  aquifers: Aquifer[]
): Map<string, string[][]> {
  const rows = aquifers.map((a) => {
    const row = [
      a.name,
      String(a.porosity),
      String(a.wiltingPoint),
      String(a.fieldCapacity),
      String(a.conductivity),
      String(a.conductSlope),
      String(a.tensionSlope),
      String(a.upperEvapFrac),
      String(a.lowerEvapDepth),
      String(a.gwRecession),
      String(a.bottomElev),
      String(a.waterTableElev),
      String(a.upperMoisture),
    ];
    if (a.evapPattern) row.push(a.evapPattern);
    return row;
  });
  return writeOrDeleteSection(raw, "AQUIFERS", rows);
}

export function nextAvailableAquiferName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "AQ");
}
